import { RecurringEditScopeDialog } from "@/components/calendars/RecurringEditScopeDialog.tsx";
import type { RecurringEditScope } from "@/types/events.ts";
import { type ReactNode, useCallback, useRef, useState } from "react";

/**
 * Promise-returning wrapper around the imperative-callback-driven
 * RecurringEditScopeDialog so consumers (notably useEventDrag) can `await`
 * a scope choice in a linear control flow.
 *
 * Returned `dialog` element must be rendered somewhere inside the caller's
 * React tree. `askForScope()` returns a Promise that resolves with the chosen
 * scope, or `null` if the user dismisses the dialog without confirming.
 */
export function useRecurringScopePromise(): {
  dialog: ReactNode;
  askForScope: () => Promise<RecurringEditScope | null>;
} {
  const [open, setOpen] = useState(false);
  const resolveRef = useRef<((scope: RecurringEditScope | null) => void) | null>(null);

  const askForScope = useCallback(() => {
    return new Promise<RecurringEditScope | null>((resolve) => {
      resolveRef.current = resolve;
      setOpen(true);
    });
  }, []);

  const handleConfirm = useCallback((scope: RecurringEditScope) => {
    setOpen(false);
    resolveRef.current?.(scope);
    resolveRef.current = null;
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    resolveRef.current?.(null);
    resolveRef.current = null;
  }, []);

  const dialog = (
    <RecurringEditScopeDialog
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      mode="edit"
    />
  );

  return { dialog, askForScope };
}
